import React, { useState, useEffect } from 'react';
import { Alert as MuiAlert, Snackbar, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

/**
 * Componente de alerta flexible que puede mostrarse dentro de un contenedor o como un Snackbar
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.severity - Nivel de severidad ('error', 'warning', 'info', 'success')
 * @param {string} props.message - Mensaje a mostrar
 * @param {boolean} props.open - Si la alerta está visible
 * @param {Function} props.onClose - Función para cerrar la alerta
 * @param {boolean} props.fixed - Si la alerta debe mostrarse en un lugar fijo (por defecto: false = snackbar)
 * @param {number} props.autoHideDuration - Duración en ms antes de cerrarse automáticamente (0 para no cerrar)
 * @param {Object} props.action - Componente de acción adicional
 * @param {string} props.variant - Variante de la alerta ('filled', 'outlined', 'standard')
 * @param {Object} props.sx - Estilos adicionales
 */
const Alert = ({
  severity = 'info',
  message,
  open = true,
  onClose,
  fixed = false,
  autoHideDuration = 6000,
  action,
  variant = 'filled',
  sx = {}
}) => {
  const [isOpen, setIsOpen] = useState(open);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    
    setIsOpen(false);
    
    if (onClose) {
      onClose();
    }
  };

  // Acción por defecto (botón de cerrar)
  const defaultAction = (
    <IconButton
      size="small"
      aria-label="close"
      color="inherit"
      onClick={handleClose}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );

  // Si no hay mensaje, no mostrar nada
  if (!message) {
    return null;
  }

  const alertContent = (
    <MuiAlert
      elevation={fixed ? 1 : 6}
      variant={variant}
      severity={severity}
      action={action || defaultAction}
      sx={{
        width: '100%',
        alignItems: 'center',
        ...sx
      }}
    >
      {typeof message === 'string' ? (
        <Typography variant="body2">{message}</Typography>
      ) : (
        message
      )}
    </MuiAlert>
  );

  // Si es fijo, mostrar directamente el alert
  if (fixed) {
    return isOpen ? alertContent : null;
  }

  // Si no es fijo, mostrar como Snackbar
  return (
    <Snackbar
      open={isOpen}
      autoHideDuration={autoHideDuration || null}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
    >
      {alertContent}
    </Snackbar>
  );
};

export default Alert;